export type BrandId = 'vesta'

export interface BrandImages {
  hero: string
  night: string
  together: string
  clinician: string
  labKit: string
  /** Open Graph / Twitter card shown when the site is shared as a link. */
  share: string
  /** Per-care-path overrides, keyed by slug. Falls back to the shared data. */
  carePaths: Record<string, string>
  /** Per-treatment overrides, keyed by slug. Falls back to the shared data. */
  treatments: Record<string, string>
}

export interface BrandCopy {
  utilityLeft: string
  utilityRight: string
  heroTitle: [string, string]
  heroSubhead: string
  manifesto: string
  ctaIntro: string
  metaTitle: string
  metaDescription: string
  legalName: string
}

export interface Brand {
  id: BrandId
  name: string
  tagline: string
  wordmark: string
  wordmarkCase: 'upper' | 'title'
  monogram: string
  sealTop: string
  sealBottom: string
  mark: 'ring' | 'leaf' | 'flame'
  /** Inline SVG markup for the browser tab icon, encoded as a data URI at runtime. */
  favicon: string
  copy: BrandCopy
  images: BrandImages
}

const VESTA_FAVICON = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">
  <rect width="32" height="32" rx="5" fill="#3A2232"/>
  <path d="M16 6C16 6 10.5 11 10.5 17.2C10.5 21 13 24 16 24C19 24 21.5 21 21.5 17.2C21.5 14.6 19.8 12.4 19.8 12.4C19.8 12.4 19.2 14.6 17.6 15C18.4 12 16 6 16 6Z" fill="#C9603F"/>
</svg>`

const IMAGES = {
  hero: 'https://cdn.magicpatterns.com/patterns/generated-images/7ee1d032-b683-40a1-8715-5b04977dfa09.jpg',
  night: 'https://cdn.magicpatterns.com/patterns/generated-images/d36a1bde-1090-4f1f-ba2f-3b64fc75870e.jpg',
  together:
    'https://cdn.magicpatterns.com/patterns/generated-images/2fe002d8-9e53-4603-a692-99bb4030a2b4.jpg',
  labKit:
    'https://cdn.magicpatterns.com/patterns/generated-images/1a7b07cf-ece3-4753-852c-205ec8dd1020.jpg',
  clinician:
    'https://cdn.magicpatterns.com/patterns/generated-images/35d0b2c1-ff93-4c83-b570-ddca6cd5181e.jpg',
}

export const brands: Record<BrandId, Brand> = {
  vesta: {
    id: 'vesta',
    name: 'Vesta',
    tagline: 'The questions no one answered',
    wordmark: 'Vesta',
    wordmarkCase: 'title',
    monogram: 'V',
    sealTop: 'THE QUESTIONS',
    sealBottom: 'NO ONE ANSWERED',
    mark: 'flame',
    favicon: VESTA_FAVICON,
    copy: {
      utilityLeft: 'Menopause Society–certified clinicians · Licensed in all 50 states',
      utilityRight: 'The symptom decoder is free. No email, no account.',
      heroTitle: ['You’re not', 'imagining it.'],
      heroSubhead:
        'Perimenopause has more than thirty recognised symptoms. Most women are told it’s stress, or that they’re too young, or that their labs came back normal. We’ll tell you what is actually happening in your body — and then we’ll treat it.',
      manifesto: 'Someone should have told you years ago.',
      ctaIntro:
        'You don’t have to arrive with a diagnosis, a symptom diary, or the right words. Start with the one thing that’s bothering you most. A clinician reads every answer before anything is prescribed or charged.',
      metaTitle: 'Vesta — The questions no one answered.',
      metaDescription:
        'Perimenopause and menopause care for women who were told it was nothing. A free symptom decoder, honest education, facilitated circles, and Menopause Society–certified clinicians.',
      legalName: 'Vesta Women’s Health, Inc.',
    },
    images: {
      ...IMAGES,
      share: IMAGES.hero,
      carePaths: {},
      treatments: {},
    },
  },
}
